import { useCallback, useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "@vilocnv/allsetra-core";
import { useAppDispatch, useAppSelector, useCurrentUser } from "hooks";
import { selectTripBackupReminderStatus } from "app/data/selectors/tripBackupReminderSelectors";
import { selectAccountFeaturesState } from "app/data/selectors";
import {
  checkShouldShowReminderThunk,
  dismissReminderThunk
} from "app/features/trip-backup-reminder/tripBackupReminderActions";
import AuditService, {
  TripBackupAuditAction,
  TripBackupAuditPage
} from "app/data/services/AuditService";
import { TripBackupReminderCache } from "app/data/utils/TripBackupReminderCache";
import { ADMIN_ROLES } from "app/data/helpers/rolePermissions";

export type TripBackupReminderPage = 1 | 2;

export const useTripBackupReminder = (accountId: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<TripBackupReminderPage>(1);
  const [hasViewedXarPage, setHasViewedXarPage] = useState(false);
  const reminderStatus = useAppSelector(selectTripBackupReminderStatus);
  const { features } = useAppSelector(selectAccountFeaturesState);
  const { currentRoleName } = useCurrentUser();
  const hasCheckedRef = useRef(false);
  const lastAccountIdRef = useRef<string | null>(null);
  const hasViewedXarPageRef = useRef(false);

  const showXarPage = Boolean(features.isKeurmerkEnabled);
  const isAdmin = Boolean(
    currentRoleName && ADMIN_ROLES.includes(currentRoleName)
  );
  const showXarCta = showXarPage && isAdmin;

  const fetchAndCheckReminder = useCallback(async () => {
    if (!accountId) return;

    if (hasCheckedRef.current && lastAccountIdRef.current === accountId) {
      return;
    }

    hasCheckedRef.current = true;
    lastAccountIdRef.current = accountId;

    try {
      await dispatch(checkShouldShowReminderThunk(accountId)).unwrap();
    } catch (error) {
      console.error("Error fetching reminder status:", error);
    }
  }, [dispatch, accountId]);

  const getPagesShown = useCallback((): TripBackupAuditPage[] => {
    if (showXarPage && hasViewedXarPageRef.current) {
      return ["backup", "xar"];
    }
    return ["backup"];
  }, [showXarPage]);

  const logAuditAction = useCallback(
    async (action: TripBackupAuditAction) => {
      try {
        await AuditService.logTripBackupAction({
          accountId,
          action,
          pagesShown: getPagesShown(),
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error("Failed to log audit action:", error);
      }
    },
    [accountId, getPagesShown]
  );

  const handleDismiss = useCallback(
    async (options?: { navigatedToXar?: boolean }) => {
      if (!accountId) return;

      // Require viewing the XAR page before dismiss when Keurmerk awareness is shown
      if (showXarPage && !hasViewedXarPageRef.current) {
        return;
      }

      const action: TripBackupAuditAction = options?.navigatedToXar
        ? "xarAwarenessNavigate"
        : getPagesShown().includes("xar")
          ? "xarAwarenessDismiss"
          : "dismiss";

      try {
        await dispatch(dismissReminderThunk(accountId)).unwrap();
        logAuditAction(action);
        setOpen(false);
        toast.info(
          "You'll be reminded to backup your trip registration data in three months"
        );
      } catch (error) {
        // If API fails, save to localStorage as fallback
        console.warn(
          "Failed to dismiss via API, saving to localStorage as fallback:",
          error
        );
        TripBackupReminderCache.saveAction(accountId, "dismiss");
        logAuditAction(action);
        setOpen(false);
        toast.info(
          "You'll be reminded to backup your trip registration data in three months"
        );
      }
    },
    [accountId, dispatch, getPagesShown, logAuditAction, showXarPage]
  );

  const handleNext = useCallback(() => {
    hasViewedXarPageRef.current = true;
    setHasViewedXarPage(true);
    setPage(2);
  }, []);

  const handleBack = useCallback(() => {
    setPage(1);
  }, []);

  const handleGoToXarReports = useCallback(async () => {
    hasViewedXarPageRef.current = true;
    setHasViewedXarPage(true);
    navigate("/dashboard/reports/xar-reports");
    await handleDismiss({ navigatedToXar: true });
  }, [navigate, handleDismiss]);

  useEffect(() => {
    if (lastAccountIdRef.current && lastAccountIdRef.current !== accountId) {
      hasCheckedRef.current = false;
    }
  }, [accountId]);

  useEffect(() => {
    fetchAndCheckReminder();
  }, [fetchAndCheckReminder]);

  useEffect(() => {
    if (
      reminderStatus.shouldShow &&
      !reminderStatus.loading &&
      !reminderStatus.error
    ) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [reminderStatus.shouldShow, reminderStatus.loading, reminderStatus.error]);

  useEffect(() => {
    if (open) {
      setPage(1);
      setHasViewedXarPage(false);
      hasViewedXarPageRef.current = false;
    }
  }, [open]);

  return {
    open,
    theme,
    page,
    hasViewedXarPage,
    showXarPage,
    showXarCta,
    isAdmin,
    handleDismiss,
    handleNext,
    handleBack,
    handleGoToXarReports
  };
};
